import { generateClient } from "aws-amplify/api";
import dayjs from "dayjs";
import {
  getUser as graphqlGetUser,
  activityLogsByStaffID,
  staffByBusinessentityID,
  storeLocationsByBusinessentityID,
  activityLogsByStorelocationID,
  userBusinessEntitiesByUserId,
  userStoreLocationsByUserId,
} from "../graphql/queries";
import {
  createActivityLog,
  updateActivityLog,
  deleteActivityLog as graphqlDeleteActivityLog,
  createStaff,
  updateStaff as graphqlUpdateStaff,
  deleteStaff as graphqlDeleteStaff,
} from "../graphql/mutations";
import {
  ActivityLog,
  ActivityLogType,
  DepartmentType,
  Staff,
  StoreLocation,
  User,
  UserToBusinessEntity,
  UserToStoreLocation,
} from "./models";

interface UserBusinessEntitiesByUserIdType {
  data: { staffByBusinessentityID: { items: Staff } };
}

const client = generateClient();

interface IActivityLog {
  createdAt: string;
  staffID: string;
  storelocationID: string;
  type: string;
  saleAmountCard: number;
  saleAmountCash: number;
  commissionAmountCard: number;
  commissionAmountCash: number;
  tipAmountCard: number;
  tipAmountCash: number;
}

export const getUser = async (userID: string): Promise<User> => {
  const response = (await client.graphql({
    query: graphqlGetUser,
    variables: {
      id: userID,
    },
  })) as { data: { getUser: User } };

  return response.data.getUser;
};

export const deleteActivityLog = async (
  activityLogID: string
): Promise<any> => {
  const response = client.graphql({
    query: graphqlDeleteActivityLog,
    variables: {
      input: {
        id: activityLogID,
      },
    },
  });
  return await response;
};

export const addStaff = async (
  name: string,
  department: DepartmentType,
  businessEntityID: string,
  hourlyRate: number,
  pin: string,
  email?: string | null,
  phoneNumber?: string | null
): Promise<any> => {
  const response = client.graphql({
    query: createStaff,
    variables: {
      input: {
        name: name,
        email: email,
        phoneNumber: phoneNumber,
        department: department,
        hourlyRate: hourlyRate,
        pin: pin,
        businessentityID: businessEntityID,
      },
    },
  });
  return await response;
};

export const deleteStaff = async (
  staff: Staff,
): Promise<any> => {
  const response = client.graphql({
    query: graphqlDeleteStaff,
    variables: {
      input: {
        id: staff.id
      },
    },
  });
  return await response;
};

export const updateStaff = async (staff: Staff): Promise<Staff> => {
  const response = (await client.graphql({
    query: graphqlUpdateStaff,
    variables: {
      input: {
        id: staff.id,
        name: staff.name,
        email: staff.email,
        phoneNumber: staff.phoneNumber,
        department: staff.department,
        hourlyRate: staff.hourlyRate,
        pin: staff.pin,
      },
    },
  })) as { data: { updateStaff: Staff } };
  return response.data.updateStaff;
};

export const addOrUpdateActivityLog = async (
  activityLog: ActivityLog
): Promise<any> => {
  const response = client.graphql({
    query: activityLog.id ? updateActivityLog : createActivityLog,
    variables: {
      input: {
        id: activityLog.id,
        createdAt: activityLog.createdAt,
        staffID: activityLog.staffID,
        storelocationID: activityLog.storeLocationID,
        type: activityLog.type.toString(),
        saleAmountCard: activityLog.saleAmountCard,
        saleAmountCash: activityLog.saleAmountCash,
        commissionAmountCard: activityLog.commissionAmountCard,
        commissionAmountCash: activityLog.commissionAmountCash,
        tipAmountCard: activityLog.tipAmountCard,
        tipAmountCash: activityLog.tipAmountCash,
        giftCardAmount: activityLog.giftCardAmount,
        depositAmount: activityLog.depositAmount,
      },
    },
  });
  return await response;
};

export const getStoreLocationsForBusinessEntity = async (
  businessEntityId: string
): Promise<StoreLocation[]> => {
  const response = (await client.graphql({
    query: storeLocationsByBusinessentityID,
    variables: {
      businessentityID: businessEntityId,
    },
  })) as {
    data: { storeLocationsByBusinessentityID: { items: StoreLocation[] } };
  };

  return response.data.storeLocationsByBusinessentityID.items;
};

export const getStoreLocationsForUser = async (
  userId: string
): Promise<UserToStoreLocation[]> => {
  const response = (await client.graphql({
    query: userStoreLocationsByUserId,
    variables: {
      userId: userId,
    },
  })) as {
    data: { userStoreLocationsByUserId: { items: UserToStoreLocation[] } };
  };

  return response.data.userStoreLocationsByUserId.items;
};

export const getStaffsForBusinessEntity = async (
  businessEntityID: string
): Promise<Staff[]> => {
  const response = (await client.graphql({
    query: staffByBusinessentityID,
    variables: {
      businessentityID: businessEntityID,
    },
  })) as { data: { staffByBusinessentityID: { items: Staff[] } } };

  return response.data.staffByBusinessentityID.items.map((item): Staff => {
    return {
      ...item,
      department:
        DepartmentType[
          item.department?.toString() as keyof typeof DepartmentType
        ],
    };
  });
};

export const getActivityLogsByStoreLocationID = async (
  storeLocationID: string,
  date: dayjs.Dayjs
): Promise<ActivityLog[]> => {
  const response = (await client.graphql({
    query: activityLogsByStorelocationID,
    variables: {
      storelocationID: storeLocationID,
      filter: {
        createdAt: {
          gt: date.startOf("day"),
          lt: date.add(1, "day").startOf("day").toISOString(),
        },
      },
    },
  })) as {
    data: {
      activityLogsByStorelocationID: { items: ActivityLog[] };
    };
  };
  return response.data.activityLogsByStorelocationID.items.map(
    (item): ActivityLog => {
      return {
        ...item,
        type: ActivityLogType[
          item.type.toString() as keyof typeof ActivityLogType
        ],
      };
    }
  );
};

export const getActivityLogsByStaff = async (
  staffID: string,
  minDate: dayjs.Dayjs,
  maxDate: dayjs.Dayjs
): Promise<ActivityLog[]> => {
  const response = (await client.graphql({
    query: activityLogsByStaffID,
    variables: {
      staffID: staffID,
      filter: {
        createdAt: { gt: minDate.toISOString(), lt: maxDate.toISOString() },
      },
    },
  })) as {
    data: {
      activityLogsByStaffID: { items: ActivityLog[] };
    };
  };
  return response.data.activityLogsByStaffID.items.map((item: any): ActivityLog => {
    return {
      ...item,
      storeLocationID: item.storelocationID,
      type: ActivityLogType[
        item.type.toString() as keyof typeof ActivityLogType
      ],
    };
  });
};

export const getActivityLogsByStaffIDWeekToDate = async (
  staffID: string
): Promise<ActivityLog[]> => {
  const response = (await client.graphql({
    query: activityLogsByStaffID,
    variables: {
      staffID: staffID,
      filter: { createdAt: { gt: dayjs().startOf("week").toISOString() } },
    },
  })) as {
    data: {
      activityLogsByStaffID: { items: ActivityLog[] };
    };
  };
  return response.data.activityLogsByStaffID.items.map((item): ActivityLog => {
    return {
      ...item,
      type: ActivityLogType[
        item.type.toString() as keyof typeof ActivityLogType
      ],
    };
  });
};

export const getBusinessEntityIDOfUser = async (
  userID: string
): Promise<UserToBusinessEntity> => {
  const response = (await client.graphql({
    query: userBusinessEntitiesByUserId,
    variables: {
      userId: userID,
    },
  })) as {
    data: { userBusinessEntitiesByUserId: { items: UserToBusinessEntity[] } };
  };

  return response.data.userBusinessEntitiesByUserId.items[0];
};
